import { IAbstractClass } from 'library/ts/interfaces/abstract/IAbstractClass';
import { IBaseConfig } from 'library/ts/interfaces/config/IBaseConfig';
import { IImportable } from 'library/ts/interfaces/importable/IImportable';
import { IImporterReject } from 'library/ts/interfaces/importer/IImporterReject';
import { IImporterResolve } from 'library/ts/interfaces/importer/IImporterResolve';
import { IImportVO } from 'library/ts/interfaces/importer/IImportVO';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { IPluginConfig } from 'library/ts/interfaces/plugin/IPluginConfig';
import { ConfigModuleCategory } from 'library/ts/model/enums/config/ConfigModuleCategory';
import { ImportType } from 'library/ts/model/enums/config/ImportType';
import { ImportVO } from 'library/ts/model/vo/ImportVO';
import { IAbstractPluginArgument } from 'library/ts/plugins/shared/interfaces/arguments/IAbstractPluginArgument';
import { IAppendPluginDomContainerArgument } from 'library/ts/plugins/shared/interfaces/arguments/IAppendPluginDomContainerArgument';
import { ILoadPluginScriptsArgument } from 'library/ts/plugins/shared/interfaces/arguments/ILoadPluginScriptsArgument';
import { ISetupPluginsForOwnerClassArgument } from 'library/ts/plugins/shared/interfaces/arguments/ISetupPluginsForOwnerClassArgument';
import { TPluginsDictionary } from 'library/ts/plugins/shared/types/TPluginsDictionary';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';

const logger: ILogger = LogService.create( { prefixes: [ 'SmartTag:', 'PluginUtilities:' ] } );

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Plugin Utilities class for agnostic plugins setup
 */
export class PluginUtilities {

	/**
	 * @description Returns a dictionary of plugin configs for the owner class name: i.e. 'View'
	 * @param {IAbstractClass} ownerClass - a class owner of the plugin
	 * @param {IBaseConfig} config - app configuration - should have modules with <code>ConfigModuleCategory.PLUGIN</code> category
	 * @returns {TPluginsDictionary | undefined}
	 */
	public static getPluginsDictionaryForOwnerClass = (
		ownerClass: IAbstractClass,
		config: IBaseConfig
	): TPluginsDictionary | undefined => {
		const utils = window?.SmartTag?.utilities;

		Object.keys( config ).forEach(
			( key ) => {
				const module: IPluginConfig = config[ key ] as IPluginConfig;

				if ( module?.category === ConfigModuleCategory.PLUGIN ) {
					if ( module?.params?.owner === ownerClass?.name ) {
						// create object for storing plugin configs
						if ( utils.Type.isUndefined( ownerClass.plugins ) ) ownerClass.plugins = {};
						// if owner has no reference to a plugin => initialize it as empty object
						if ( utils.Type.isUndefined( ownerClass.plugins[ key ] ) ) {
							// init + store plugin config module in output
							ownerClass.plugins[ key ] = {} as IPluginConfig;
						}
						// set plugin name
						ownerClass.plugins[ key ].name = key;
						// clone plugin config module so we don't expose it via original config reference
						ownerClass.plugins[ key ] = utils.DeepMerge.deepmerge( ownerClass.plugins[ key ], module );
						// get + store plugin namespace from DOM so we can instantiate it later...
						ownerClass.plugins[ key ].object = utils.Object.getDeepVal( window, ownerClass.plugins[ key ].params.import.app.namespace );
					}
				}
			}
		);

		return ownerClass.plugins;
	};

	/**
	 * @description Setup plugin(s) for an owner class
	 * @param {ISetupPluginsForOwnerClassArgument} argument
	 * @returns {Promise<IPromiseResolveGeneric & {plugins: TPluginsDictionary}>}
	 */
	public static setupPluginsForOwnerClass = (
		argument: ISetupPluginsForOwnerClassArgument = {
			ownerClass: undefined,
			framework: undefined,
			config: undefined
		}
	): Promise<IPromiseResolveGeneric & { plugins: TPluginsDictionary }> => {
		const ownerClass = argument?.ownerClass;
		const framework = argument?.framework;
		const config = argument?.config;
		return new Promise(
			( resolve, reject ) => {
				const utils = window?.SmartTag?.utilities;

				// get plugins for owner class
				ownerClass.plugins = PluginUtilities.getPluginsDictionaryForOwnerClass( ownerClass, config );

				// INFO: reduce console noise
				// logger.log( ownerClass.uuid, ownerClass.name, 'setupPlugins:', ownerClass.plugins );

				// check if we have any plugins specified
				if ( utils.Type.isObject( ownerClass.plugins ) && Object.keys( ownerClass.plugins ).length > 0 ) {
					logger.log( ownerClass.uuid, ownerClass.name, 'setupPlugins:', 'Plugin configs were found => check if plugins are enabled and loaded + instantiate:', ownerClass.plugins );
					// loop over plugin configs + instantiate them
					Object.keys( ownerClass.plugins ).forEach(
						( key ) => {
							const pluginConfig: IPluginConfig = ownerClass.plugins[ key ];

							// if plugin class reference is in DOM (plugin was loaded) + if plugin was not yet instantiated (instance is undefined)
							if ( utils.Type.isFunction( pluginConfig.object ) && utils.Type.isUndefined( pluginConfig.instance ) ) {
								// instantiate plugin class
								pluginConfig.instance = new pluginConfig.object(
									{
										framework: framework,
										config: pluginConfig,
										owner: ownerClass
									} as IAbstractPluginArgument
								);
							}
						}
					);

					utils.Delay.delay( undefined )
						.then(
							() => {
								resolve( { success: true, plugins: ownerClass.plugins, msg: `Plugins for ${ownerClass.name} were instantiated!` } );
							}
						);

				} else {
					const msg: string = `No plugins configs were found for ${ownerClass.name}. Exit without instantiation!`;
					// INFO: reduce console noise
					// logger.log( ownerClass.uuid, ownerClass.name, 'setupPlugins:', msg );
					resolve( { success: false, plugins: ownerClass.plugins, msg: msg } );
				}
			}
		);

	};

	/**
	 * @description Loads (preloads) plugin scripts
	 * @param {ILoadPluginScriptsArgument} argument
	 * @returns {Promise<(IImporterResolve & {msg?: string}) | (IImporterReject & {msg?: string})>}
	 */
	public static loadPluginScripts = (
		argument: ILoadPluginScriptsArgument = {
			uuid: undefined,
			config: undefined,
			ignorePreloadFlag: false,
			ownerClassName: undefined
		}
	): Promise<IImporterResolve & { msg?: string } | IImporterReject & { msg?: string }> => {
		const uuid = argument?.uuid;
		const config = argument?.config;
		const ignorePreloadFlag = argument?.ignorePreloadFlag || false;
		const ownerClassName = argument?.ownerClassName;
		const brand = argument?.brand;
		const runtimeVO = window?.SmartTag?.runtimeVO;
		const utils = window?.SmartTag?.utilities;

		return new Promise(
			( resolve, reject ) => {
				// INFO: reduce console noise
				// logger.log( uuid, 'preloadPlugins', 'config:', config );

				const imports: IImportVO[] | undefined = [];

				// check if we have any plugins to preload
				Object.keys( config ).forEach(
					( moduleName ) => {
						const module: IPluginConfig = config[ moduleName ] as IPluginConfig;

						if ( utils.Type.isEmpty( module?.category ) === false && utils.Type.isString( module?.category ) ) {

							// check conditions needed for plugin preload...
							if (
								module?.category === ConfigModuleCategory.PLUGIN
								&&
								module?.enabled === true
								&&
								module?.params?.owner === ownerClassName
								&&
								(
									( module as IPluginConfig )?.params?.preload === true
									||
									( ( module as IPluginConfig )?.params?.preload === false && ignorePreloadFlag === true )
								)
							) {
								logger.log( uuid, 'preloadPlugins', 'Plugin to preload:', moduleName, module );

								// check if we have import assets for our plugin
								if ( utils.Type.isEmpty( module?.params?.import ) === false && utils.Type.isObject( module?.params?.import ) ) {
									Object.keys( module.params.import ).forEach(
										( key ) => {
											const importable = module.params.import[ key ] as IImportable;

											// if importable is enabled
											if ( importable.enabled === true ) {
												// if relative loading requested => convert CSS URL to relative
												if ( runtimeVO?.readyCallback?.payload?.relative === true ) {
													importable.url.regular = utils.URL.convertToRelative( importable?.url?.regular );
													if ( brand && importable.url[ brand ] ) {
														importable.url[ brand ] = utils.URL.convertToRelative( importable.url[ brand ] );
													}
												}

												const url = importable?.url?.regular;

												// if url is valid
												if ( utils.URL.valid( url ) ) {
													const importVO = new ImportVO(
														importable.type,
														url,
														undefined,
														undefined,
														undefined,
														ConfigModuleCategory.PLUGIN,
														module?.params?.owner
													);

													// set namespace and location per import type
													importVO.namespace = ( importable.type === ImportType.SCRIPT ? importable?.namespace : undefined );
													importVO.location = ( importable.type === ImportType.SCRIPT ? window : undefined );

													// add import vo
													imports.push( importVO );

													// For CSS: also load brand-specific CSS after default (same pattern as skin brand CSS)
													if ( importable.type === ImportType.STYLE && brand && importable.url[ brand ] && utils.URL.valid( importable.url[ brand ] ) ) {
														imports.push(
															new ImportVO(
																ImportType.STYLE,
																importable.url[ brand ],
																undefined,
																undefined,
																undefined,
																ConfigModuleCategory.PLUGIN,
																module?.params?.owner
															)
														);
													}
												}
											}
										}
									);
								}
							}

						}
					}
				);

				// no plugins set for preload were found
				if ( imports.length === 0 ) {
					resolve( { msg: 'No plugins marked for pre-load were found' } as IImporterResolve ); // resolve
				}

				// we have some plugins for preload
				if ( imports.length > 0 ) {
					logger.log( uuid, 'preloadPlugins', 'Plugin imports to preload:', imports );

					imports.forEach(
						( importIO ) => {
							// ssl plugin url
							if ( runtimeVO.top.ssl ) importIO.url = utils.URL.toHTTPS( importIO.url );
							// add cache tokens
							importIO.url = utils.URL.addCacheToken(
								importIO.url, runtimeVO.debug.cacheBust
							);
						}
					);

					// load imports
					utils.Importer.import( imports, true ).then(
						( result: IImporterResolve | IImporterReject ) => {
							logger.log( uuid, 'preloadPlugins', 'preload done:', result );
							result.msg = 'preloadPlugins, preload done';
							resolve( result ); // resolve
						}
					).catch(
						( error ) => {
							logger.warn( uuid, 'preloadPlugins', 'preload failure:', error );
						}
					);
				}

			}
		);
	};

	/**
	 * @description Creates plugin DOM container and appends it to display list, returns reference to container
	 * <p>Should be used from within the plugin application</p>
	 * @param {IAppendPluginDomContainerArgument} argument
	 * @returns {Promise<IPromiseResolveGeneric & {container: HTMLElement}>}
	 */
	public static appendPluginDomContainer = (
		argument: IAppendPluginDomContainerArgument = {
			config: undefined,
			rootContainer: undefined
		}
	): Promise<IPromiseResolveGeneric & { container: HTMLElement }> => {
		const config = argument?.config;
		const rootContainer = argument?.rootContainer;
		return new Promise(
			( resolve, reject ) => {
				const utils = window?.PARA?.PlayerServices?.Utilities;

				const resolvePromise = ( container: HTMLElement, msg: string = `${config.name}: DOM container created` ): void => {
					resolve( { msg: msg, container: container } );
				};

				const rejectPromise = ( error: Error, msg: string = `${config.name}: DOM container creation failed` ): void => {
					reject( { msg: msg, error: error, element: undefined } );
				};

				// sanity check for CC class string in plugin config
				if ( utils.Type.isEmpty( config?.params?.css?.container ) ) {
					// resolve promise
					utils.Delay.delay().then( () => rejectPromise( new Error( `${config.name}: DOM container creation failed: No CSS container string was found. Return undefined` ) ) );
				}

				// create plugin root container
				const container: HTMLElement = utils.DOM.stringToElement( `<div class='${config?.params?.css?.container}'></div>` );

				let methodName: string;
				let referenceElementClass: string;

				// start calculating reference element class name + insert method name

				if ( !utils.Type.isEmpty( config?.params?.css?.insertAfter ) && utils.Type.isString( config.params.css.insertAfter ) ) {
					methodName = 'insertAfter';
					referenceElementClass = config.params.css.insertAfter;
				}

				if ( !utils.Type.isEmpty( config?.params?.css?.insertBefore ) && utils.Type.isString( config.params.css.insertBefore ) ) {
					methodName = 'insertBefore';
					referenceElementClass = config.params.css.insertBefore;
				}

				// conditions met for appending plugin container to parent container
				if ( utils.Type.isEmpty( referenceElementClass ) && utils.Type.isEmpty( methodName ) ) {
					rootContainer.appendChild( container );
					utils.Delay.delay().then( () => resolvePromise( container ) );
				}

				// otherwise, process selected method name insertAfter or insertBefore
				// get reference element from DOM
				const referenceElement: HTMLElement = rootContainer.querySelector( '.' + referenceElementClass );
				// check if we have reference element and return undefined if we do not
				if ( referenceElement === null ) {
					// reject promise
					utils.Delay.delay().then( () => rejectPromise( new Error( `${config?.name}: DOM container creation failed: No reference element was found. Return undefined` ) ) );
				} else {
					// otherwise, append container
					utils.DOM[ methodName ]( container, referenceElement );
					// resolve promise
					utils.Delay.delay().then( () => resolvePromise( container ) );
				}
			}
		);
	};

}